import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import EnquiryForm from "@/components/EnquiryForm";
import { ORG_NEEDS } from "@/data/academy";
import { MEDIA } from "@/lib/media";

const STEPS = [
  { n: "01", t: "Scope", b: "Whether you're restoring landscapes after mining, rebuilding habitat after fire, improving biodiversity, establishing a seed bank, strengthening ranger capability or developing a native seed enterprise, every SeedKeepers program begins with a scoping conversation. Together, we'll understand your objectives, assess your current capability and design a practical program that supports the outcomes you're working towards." },
  { n: "02", t: "Practical needs assessment", b: "We assess species priorities, seasons, people, systems and facilities against real objectives." },
  { n: "03", t: "Staged proposal", b: "We design the learning, systems and support around the work, in realistic stages." },
];

export default function ForOrganisations() {
  return (
    <div data-testid="organisations-page">
      <PageHero
        kicker="For organisations"
        title="Building Native Seed Capability Across Your Organisation"
        subtitle="Whether you're supporting ranger teams, Traditional Owner corporations, councils, consultancies, nurseries or rehabilitation projects, our experienced practitioners work with you to design practical learning programs, on-country support and seed system solutions. Get started building the capability your organisation needs — today and into the future."
        image={MEDIA.plantingGroup}
      />

      <section className="bg-cream py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Reveal className="mb-12"><p className="overline text-ochre mb-6">How we help</p></Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-3">
            {ORG_NEEDS.map((o, i) => (
              <Reveal key={o} delay={(i % 4) * 0.05}>
                <div className="flex items-baseline gap-3 py-4 border-b border-charcoal/12">
                  <span className="font-serif text-ochre">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-charcoal/80 font-light text-lg">{o}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-charcoal text-cream py-24 md:py-32">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          <Reveal className="mb-14 max-w-2xl">
            <p className="overline text-ochre mb-6">Where we begin</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light leading-tight">A conversation, then a plan.</h2>
          </Reveal>
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.05}>
              <div className="grid grid-cols-1 md:grid-cols-[110px_1fr] gap-6 md:gap-14 items-start border-t border-cream/15 py-9">
                <span className="font-serif text-5xl md:text-6xl text-ochre/80 leading-none">{s.n}</span>
                <div>
                  <h3 className="font-serif text-2xl md:text-3xl mb-3">{s.t}</h3>
                  <p className="text-cream/70 text-lg font-light leading-relaxed max-w-2xl">{s.b}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-cream py-24 md:py-32" data-testid="org-enquiry">
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          <Reveal className="mb-10 text-center">
            <p className="overline text-ochre mb-4">Book a scoping call</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-charcoal">Let's start with the work.</h2>
          </Reveal>
          <Reveal><EnquiryForm formType="organisation" title="Organisation enquiry" cta="Book a Scoping Call" /></Reveal>
        </div>
      </section>
    </div>
  );
}
